import React, { useState } from "react";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import Button, { ButtonTypes } from "../../../../../components/Button";
import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import { FocusableTooltip } from "../../../../../components/FocusableTooltip";
import { KebabButton } from "../../../../../components/KebabButton";
import { valuationMessageFor } from "../../../../../services/valuation";
import {
  DateFormat,
  durationOf,
  formatDate,
  formatCurrency,
} from "../../../../../utils";
import { AccountComposition } from "../ValueComposition";
import { TRACKING_MODE_LABELS } from "../../../constants";
import {
  accountSnapshotOf,
  accountSubtitleOf,
  isManualMode,
  MANUAL_GAIN_TOOLTIP,
} from "../../../utils";
import { InvestmentActionSheet } from "../InvestmentActionSheet";
import { IrrFigure } from "../IrrFigure";
import { InvestmentHeroProps } from "./types";
import {
  HeroCard,
  HeroGrid,
  LeftColumn,
  RightColumn,
  IdentityRow,
  HeroName,
  HeroStatusBadge,
  HeroModeBadge,
  HeroSubtitle,
  ValueBlock,
  ValueLabel,
  ValueAmount,
  GainRow,
  GainChip,
  MetricsPanel,
  MetricsGroup,
  MetricItem,
  MetricLabel,
  MetricValue,
  ActionsRow,
  KebabWrapper,
  DesktopComposition,
  MobileComposition,
} from "./styles";

const InvestmentHeroComponent: React.FC<InvestmentHeroProps> = ({
  investment,
  investmentType,
  lastUpdated,
  contributions,
  dropdownActions,
  onAddProgress,
  onAddHolding,
  onMoveCash,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Still derived rather than assumed: the page routes a ledger account to `LedgerHeader`, but the
  // one frame before `trackingMode` arrives reaches here with neither mode settled.
  const isManual = isManualMode(investment.trackingMode);
  const modeLabel = investment.trackingMode
    ? TRACKING_MODE_LABELS[investment.trackingMode]
    : null;

  // Shared with `LedgerHeader` so this page cannot call a pending value "Unavailable"
  // while the portfolio list one click away shows it as a dash.
  const { presentedValue, isPriced, valueText } = accountSnapshotOf(investment);
  // The gain row and the composition need the earned figure too, which is a
  // narrower question than whether the value itself is showable.
  const hasCurrentValuation = isPriced && investment.netGain !== null;
  const isLoss = investment.netGain !== null && investment.netGain < 0;
  const isNegativeValue = presentedValue !== null && presentedValue < 0;

  const statusText = investment.isActive ? "Active" : "Deactivated";
  const isDeactivated = !investment.isActive;
  const subtitleText = accountSubtitleOf(investment, lastUpdated);

  const gainSign = isLoss ? "-" : "+";
  const gainText = `${gainSign}${formatCurrency(Math.abs(investment.netGain ?? 0))}`;

  const startDateText = formatDate(investment.startDate, DateFormat.DMY_SPACE);
  const institutionText = investment.institution || "—";

  const duration = durationOf(investment);

  // A manual investment's value comes from entries the owner typed, so it has no provider-price
  // provenance line and no broker total to name.
  const valueLabel = isPriced
    ? "Current value"
    : valuationMessageFor(investment.valuation);

  const compositionVisible = hasCurrentValuation;

  return (
    <>
      <HeroCard elevation={0} $isDeactivated={isDeactivated}>
        <KebabWrapper>
          <KebabButton
            aria-label="Investment actions"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          />
        </KebabWrapper>
        <InvestmentActionSheet
          name={investment.name}
          subtitle={subtitleText}
          isOpen={menuOpen}
          dropdownActions={dropdownActions}
          onClose={() => setMenuOpen(false)}
          onAddProgress={onAddProgress}
          onAddHolding={onAddHolding}
          onMoveCash={onMoveCash}
          trackingMode={investment.trackingMode}
        />

        <HeroGrid>
          <LeftColumn>
            <IdentityRow>
              <HeroName>{investment.name}</HeroName>
              <HeroStatusBadge $isActive={investment.isActive}>
                {statusText}
              </HeroStatusBadge>
              {modeLabel && <HeroModeBadge>{modeLabel}</HeroModeBadge>}
            </IdentityRow>
            <HeroSubtitle>{subtitleText}</HeroSubtitle>

            <ValueBlock>
              <ValueLabel>{valueLabel}</ValueLabel>
              <ValueAmount
                $negative={isNegativeValue}
                data-testid="investment-value"
              >
                {valueText}
              </ValueAmount>
              {hasCurrentValuation && (
                <GainRow>
                  <FocusableTooltip title={MANUAL_GAIN_TOOLTIP}>
                    <GainChip $negative={isLoss} data-testid="investment-gain">
                      {isLoss ? <TrendingDownIcon /> : <TrendingUpIcon />}
                      {gainText}
                    </GainChip>
                  </FocusableTooltip>
                  <IrrFigure
                    irr={investment.irr}
                    isIrrAnnualized={investment.isIrrAnnualized}
                    basisNote={MANUAL_GAIN_TOOLTIP}
                  />
                </GainRow>
              )}
            </ValueBlock>

            <MetricsPanel>
              <MetricsGroup>
                <MetricItem>
                  <MetricLabel>Investment type</MetricLabel>
                  <MetricValue data-testid="investment-type">
                    {investmentType}
                  </MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Institution</MetricLabel>
                  <MetricValue>{institutionText}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Start date</MetricLabel>
                  <MetricValue>{startDateText}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Duration</MetricLabel>
                  <MetricValue>{duration}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Contributions</MetricLabel>
                  <MetricValue>{contributions}</MetricValue>
                </MetricItem>
              </MetricsGroup>
            </MetricsPanel>
          </LeftColumn>

          <RightColumn>
            <ActionsRow>
              <ActionsDropdown
                dropdownActions={dropdownActions}
                testId="investment-hero-actions"
              />
              {isManual && (
                <Button
                  type={ButtonTypes.primary}
                  buttonType="button"
                  onClick={onAddProgress}
                  aria-label="Add investment entry"
                >
                  Add entry
                </Button>
              )}
            </ActionsRow>

            {compositionVisible && (
              <DesktopComposition>
                <AccountComposition investment={investment} />
              </DesktopComposition>
            )}
          </RightColumn>
        </HeroGrid>
      </HeroCard>

      {compositionVisible && (
        <MobileComposition>
          <AccountComposition investment={investment} />
        </MobileComposition>
      )}
    </>
  );
};

export const InvestmentHero = React.memo(InvestmentHeroComponent);
